import {
  Form,
  Modal,
  Input,
  Select,
  Alert,
  Typography,
  InputNumber,
  Row,
  Col,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { AuthenticationContext } from "../../../contexts/authentication-context";
import { CompanyContext } from "../../../contexts/company-context";
import { EmployeeOperation } from "../../../serviceoperation/EmployeeOperation";
import { BranchOperation } from "../../../serviceoperation/BranchOperation";
import { EmployeeType } from "../../schedule/components/week/week-types";
import { LocationType } from "../../schedule/modal/location-types";
import { DepartmentOperation } from "../../../serviceoperation/DepartmentOperation";
import { Departmenttypes } from "./department-types";

interface Values {
  phone: string;
  role: number;
  firstName: string;
  lastName: string;
  email: string;
  manager: string;
  title: string;
  location: string;
  department: number;
  hourlyRate: number;
  ssn: string;
}

interface AddEmployeeFormProps {
  open: boolean;
  onCreate: (values: Values) => void;
  onCancel: () => void;
}

//AddScheduleForm
const AddEmployeeForm: React.FC<AddEmployeeFormProps> = ({
  open,
  onCreate,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const { Text } = Typography;
  const { selectedCompany } = useContext(CompanyContext);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // List of employee for a given logged in user.
  // This will contain a data regardless of who is logged in
  // as a logged in user will be in this list.
  const [employee, setEmployee] = useState<EmployeeType[]>([]);
  // Get Corporation Locations...
  const [location, setLocation] = useState<LocationType[]>([]);
  const [department, setDepartment] = useState<Departmenttypes[]>([]);

  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const [showDepart, setDepart] = useState(false);

  const roleOptions = [
    {
      value: "3",
      label: (
        <div>
          <Text strong>Employee</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Employees should have limited access, allowing them to view their
            own schedules, request time off, and update their personal
            information. They should not have access to other employees' data or
            system settings.
          </Text>
        </div>
      ),
    },
    {
      value: "2",
      label: (
        <div>
          <Text strong>Manager / Supervisor</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            This role have access to schedule creation and modification for
            employees under their supervision. They can also view and manage
            employee information but may have limited access to certain
            administrative settings.
          </Text>
        </div>
      ),
    },
    {
      value: "-80",
      label: (
        <div>
          <Text strong>Self Service</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            This role have access to use mobile app for self service Schedule
            Activity
          </Text>
        </div>
      ),
    },
    {
      value: "-1",
      label: (
        <div>
          <Text strong>Administrator / Superuser</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            This role should have full access to all features and
            functionalities of the system. Administrators can create, modify,
            and delete schedules, manage employee profiles, and have overall
            control of the system settings.
          </Text>
        </div>
      ),
    },
  ];
  const normalizeSSN = (val: string): string => {
    const digits = val.replace(/\D/g, "").slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5)
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      const employeeOps = new EmployeeOperation();
      const response = await employeeOps.getEmployeeAsync(
        selectedCompany?.corporationId as number,
        1,
        255,
      );
      setEmployee(
        response.results
          .filter((x) => x.isActive)
          .map((s) => ({
            key: s.employeeId,
            name: `${s.firstName} ${s.lastName}`,
          })),
      );
    };

    fetchEmployees();
  }, [selectedCompany]);

  const inviteEmployee = async (values: Values): Promise<boolean> => {
    setConfirmLoading(true);
    let success = false;

    const empOps = new EmployeeOperation();
    await empOps
      .inviteEmployeeAsync({
        recipient: {
          name: `${values.firstName}${values.lastName}`,
          email: values.email,
          displayName: `${values.firstName} ${values.lastName}`,
          givenName: values.firstName,
          surname: values.lastName,
          title: values.title,
          ManagerId: values.manager,
          Phone: values.phone,
          EmployeeAttribute: {
            roleId: values.role,
            departmentId: values.department,
            jobId: 1,
            salary: values.hourlyRate,
            ssn: values.ssn?.replace(/-/g, "").trim() || undefined,
            employeeType: 1,
            startDate: new Date(),
          },
        },
        sender: {
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          EmployeeId:
            userInfo?.employeeCorporation
              ?.filter(
                (x) => x.corporationId === selectedCompany?.corporationId,
              )
              .map((x) => x.employeeId)
              .at(0) || "",
        },
        CompanyInformation: {
          Name: selectedCompany?.name || "",
          CompanyId: selectedCompany?.corporationId as number,
        },
      })
      .then((x) => {
        success =
          x.statusCode != undefined && x.statusCode.toString() === "200";

        // Set Error Message for Conflict.
        if (x.statusCode != undefined && x.statusCode.toString() === "409") {
          setShowError(true);
          setErrorMessage("User with given email already exist");
        }
      });
    setConfirmLoading(false);
    return success;
  };

  useEffect(() => {
    const branchOps = new BranchOperation();
    branchOps
      .getBranchAsync(selectedCompany?.corporationId as number)
      .then((value) => {
        if (value.data) {
          setLocation(
            value.data.map((s) => ({
              key: s.branchID,
              name: s.name,
              color: s.color,
            })),
          );
        }
      });
  }, [selectedCompany]);

  // On Change of Department selection.
  const handleLocationChange = (changedValues: React.Key) => {
    const departmentOps = new DepartmentOperation();
    departmentOps
      .getDepartmentByAsync(
        selectedCompany?.corporationId as number,
        changedValues as number,
      )
      .then((value) => {
        setDepartment(
          value.data.map((s) => ({
            key: s.departmentId,
            name: s.name,
          })),
        );
      });
    // if changedValues is not null or undefined
    // Then Enable the Department fields.
    setDepart(true);
  };
  return (
    <Modal
      open={open}
      title="Invite New Employee"
      okText="Create"
      cancelText="Cancel"
      confirmLoading={confirmLoading}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      maskClosable={false}
      onOk={() => {
        setConfirmLoading(true);
        form
          .validateFields()
          .then(async (values) => {
            // Insert / Invite Employee
            // to join the company.

            if (await inviteEmployee(values)) {
              form.resetFields();
              onCreate(values);
            } else {
              setShowError(true);
              // Stay on the Page and keep the Modal open and display Error message.
            }
          })
          .catch(() => {
            setShowError(true);
            setConfirmLoading(false);
          });
        setConfirmLoading(false);
      }}
    >
      <Form
        form={form}
        layout="vertical"
        name="form_in_modal"
        initialValues={{ modifier: "public" }}
      >
        <Form.Item
          label="Employee Information"
          style={{ marginTop: 24, marginBottom: 12 }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: "Please enter first name",
                  },
                ]}
                style={{
                  margin: "0 4px",
                }}
              >
                <Input placeholder="First Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: "Please enter last name",
                  },
                ]}
                style={{
                  margin: "0 4px",
                }}
              >
                <Input placeholder="Last Name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please enter employee's email.",
                  },
                ]}
                style={{
                  //display: "inline-block",
                  //width: "calc(33% - 8px)",
                  margin: "0 4px",
                  marginTop: "8px",
                }}
              >
                <Input placeholder="Email Address" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="phone"
                style={{
                  //display: "inline-block",
                  //width: "calc(33% - 8px)",
                  margin: "0 4px",
                  marginTop: "8px",
                }}
                rules={[
                  {
                    required: true,
                    message: "Please enter employee's phone!",
                  },
                ]}
              >
                <Input placeholder="Phone Number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="title"
            style={{
              //display: "inline-block",
              //width: "calc(33% - 8px)",
              margin: "0 4px",
              marginTop: "8px",
            }}
            rules={[
              {
                required: true,
                message: "Please enter employee's title!",
              },
            ]}
          >
            <Input placeholder="Title" />
          </Form.Item>
        </Form.Item>

        <Form.Item label="Role & Department">
          <Form.Item
            name="manager"
            hasFeedback
            style={{
              //display: "inline-block",
              //width: "calc(33% - 8px)",
              margin: "0 4px",
              marginTop: "8px",
            }}
            rules={[
              {
                required: true,
                message: "Please select an manager!",
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Select Manager"
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={employee.map((emp) => {
                return {
                  value: emp.key,
                  label: emp.name,
                };
              })}
            />
          </Form.Item>
          <Form.Item
            name="role"
            hasFeedback
            style={{
              //display: "inline-block",
              //width: "calc(33% - 8px)",
              margin: "0 4px",
              marginTop: "8px",
            }}
            rules={[
              {
                required: true,
                message: "Please select a role!",
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Select a role"
              allowClear
              optionFilterProp="children"
              options={roleOptions}
            />
          </Form.Item>

          <Form.Item
            name="location"
            style={{
              //display: "inline-block",
              //width: "calc(33% - 8px)",
              margin: "0 4px",
              marginTop: "8px",
            }}
          >
            <Select
              onSelect={handleLocationChange}
              placeholder="Select Employee Branch"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={location.map((loc) => {
                return {
                  value: loc.key,
                  label: loc.name,
                };
              })}
            />
          </Form.Item>
          <Form.Item
            name="department"
            style={{
              //display: "inline-block",
              //width: "calc(33% - 8px)",
              margin: "0 4px",
              marginTop: "8px",
            }}
          >
            <Select
              placeholder="Select Employee Department"
              allowClear
              showSearch
              disabled={!showDepart}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={department.map((loc) => {
                return {
                  value: loc.key,
                  label: loc.name,
                };
              })}
            />
          </Form.Item>
        </Form.Item>

        <Form.Item
          name="hourlyRate"
          label="Hourly Pay Rate"
          style={{ margin: "0 4px", marginTop: "8px" }}
          rules={[{ required: true, message: "Please enter hourly pay rate" }]}
        >
          <InputNumber<number>
            min={0}
            style={{ width: "100%" }}
            formatter={(value) =>
              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => Number((value ?? "").replace(/\$\s?|(,*)/g, ""))}
            placeholder="e.g. 25.50"
          />
        </Form.Item>
        <Form.Item
          name="ssn"
          style={{
            //display: "inline-block",
            //width: "calc(33% - 8px)",
            margin: "0 4px",
            marginTop: "8px",
          }}
          rules={[
            { required: true, message: "Please enter SSN" },
            {
              pattern: /^\d{3}-\d{2}-\d{4}$/,
              message: "Format must be 123-45-6789",
            },
          ]}
        >
          <Input.Password
            maxLength={11}
            placeholder="SSN"
            onChange={(e) => {
              const formatted = normalizeSSN(e.target.value);
              form.setFieldsValue({ ssn: formatted });
            }}
          />
        </Form.Item>

        {/* Confirm SSN */}
        <Form.Item
          name="confirmSsn"
          style={{
            //display: "inline-block",
            //width: "calc(33% - 8px)",
            margin: "0 4px",
            marginTop: "8px",
          }}
          dependencies={["ssn"]}
          rules={[
            { required: true, message: "Please confirm SSN" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("ssn") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("SSNs do not match"));
              },
            }),
          ]}
        >
          <Input.Password
            maxLength={11}
            placeholder="Confirm SSN"
            onChange={(e) => {
              const formatted = normalizeSSN(e.target.value);
              form.setFieldsValue({ confirmSsn: formatted });
            }}
          />
        </Form.Item>
      </Form>
      {showError ? (
        <Alert
          message={
            errorMessage && errorMessage !== ""
              ? errorMessage
              : "There was an error processing your request. Please try again later."
          }
          type="error"
          showIcon
        />
      ) : null}
    </Modal>
  );
};

export default AddEmployeeForm;
